import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	isBefore,
	makeClassName,
	Modal,
	relativeTimeShort,
	RelativeTimestamp,
	Row,
	StyleProps,
	toGrammaticalNumber,
	Tooltip,
	TooltipButton,
	TruncatedText,
	useToggle,
} from "@hex-insights/core";
import { InternalLink, NavLink, PageInfo, useRouteMatch } from "@hex-insights/router";
import { EmptyView } from "../../../../Components/Empty";
import { Icon } from "../../../../Components/Icon";
import { SearchField } from "../../../../Components/Search";
import { Tile } from "../../../../Components/Tile";
import {
	LatestMessagesAndMessageGroupsListQuery,
	MessageGroup,
	MessageOrderField,
	OrderDirection,
	useLatestMessagesAndMessageGroupsListQuery,
	UserViewRecordListForTargetQuery,
	useViewRecords,
} from "../../../../Utilities";
import { MessageGroupFilterFormState } from "../../../Filter";
import { MessageGroupFormat } from "../../../Format";
import { MessageGroupIcon } from "../Icon";
import styles from "./styles.module.css";

export type MessageGroupsListProps = {
	messageGroupPageTo: (id: MessageGroup["id"]) => string;
} & Pick<CreateButtonProps, "createPageInfo" | "NewMessageModal"> &
	Partial<ClassNameProps & StyleProps>;

export function MessageGroupsList({
	messageGroupPageTo,
	createPageInfo,
	NewMessageModal,
	className,
	style,
}: MessageGroupsListProps) {
	const filterFormState = MessageGroupFilterFormState.useFilterFormState();

	const { isOn: filterArchived, toggle: toggleFilterArchived } = useToggle(false);

	const { loading, data } = useLatestMessagesAndMessageGroupsListQuery({
		variables: {
			filters: {
				messageGroup: [
					{
						search: filterFormState.formValues.search,
						or: [{ users: [{ isCurrentEQ: true }] }, { userGroups: [{ members: [{ isCurrentEQ: true }] }] }],
					},
				],
			},
			archived: filterArchived,
			order: { field: MessageOrderField.CreatedAt, direction: OrderDirection.Desc },
		},
		fetchPolicy: "cache-and-network",
		pollInterval: 30_000,
	});

	const messageGroupIDs = data?.latestMessageByGroupConnection.edges.map((e) => e.node.messageGroup.id) ?? [];
	const { loading: loadingViewRecords, viewRecordMap } = useViewRecords(messageGroupIDs);

	return (
		<Tile className={makeClassName(styles["message-group-list"], className)} style={style}>
			<Tile.Body className={styles["message-group-list__body"]}>
				<Column justify="spaced-start" className={styles["message-group-list__content"]}>
					<Row justify="space-between" align="center">
						<Heading.H1 noMargin>Chats</Heading.H1>

						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<CreateButton createPageInfo={createPageInfo} NewMessageModal={NewMessageModal} />

							<TooltipButton side="bottom">
								{React.useCallback(
									() => (
										<Tooltip isInteractable>
											<Tooltip.Body>
												<Button variant="link" size="small" onClick={toggleFilterArchived} className="nowrap">
													{filterArchived ? "View Chats" : "View Archived Chats"}
												</Button>
											</Tooltip.Body>
										</Tooltip>
									),
									[filterArchived, toggleFilterArchived],
								)}
								{React.useCallback(
									({ onClick, disabled }) => (
										<Button variant="link" size="small" onClick={onClick} disabled={disabled} className="button--icon">
											<Icon.Gear size={20} className="block" />
										</Button>
									),
									[],
								)}
							</TooltipButton>
						</Row>
					</Row>

					<Column justify="spaced-start" verticalSpacing="1.5rem" style={{ height: "100%", overflow: "hidden" }}>
						<SearchField
							formState={filterFormState}
							name="search"
							className={styles["message-group-list__search-form"]}
							fieldClassName={styles["message-group-list__search-form__search-field"]}
						/>

						<Column justify="spaced-start" overflow="scroll" style={{ height: "100%" }}>
							<Conditional>
								<If condition={!!data && data.latestMessageByGroupConnection.edges.length > 0}>
									{data?.latestMessageByGroupConnection.edges.map((e) => (
										<MessageGroupView
											key={e.node.id}
											message={e.node}
											showViewStatus={!loadingViewRecords}
											viewRecord={viewRecordMap[e.node.messageGroup.id]}
											messageGroupPageTo={messageGroupPageTo}
										/>
									))}
								</If>
								<If condition={!loading}>
									<Conditional>
										<If condition={filterFormState.formValues.search !== ""}>
											<span className={styles["message-group-list__none-message"]}>No results found</span>
										</If>
										<If condition={filterArchived}>
											<EmptyView
												IconComponent={Icon.Chat}
												iconSize={60}
												primaryText="Messages"
												secondaryText={
													<React.Fragment>Once you archive a chat, it will appear&nbsp;here</React.Fragment>
												}
											/>
										</If>
										<Else>
											<EmptyView
												IconComponent={Icon.Chat}
												iconSize={60}
												primaryText="Messages"
												secondaryText={<React.Fragment>Once you start a chat, it will appear&nbsp;here</React.Fragment>}
											/>
										</Else>
									</Conditional>
								</If>
							</Conditional>
						</Column>
					</Column>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

type CreateButtonProps = {
	createPageInfo: Pick<PageInfo<any, string, any>, "path" | "to">;
	NewMessageModal: (props: { onClose: () => void }) => JSX.Element | null;
};

function CreateButton({ createPageInfo, NewMessageModal }: CreateButtonProps) {
	const isOnCreatePage = !!useRouteMatch(createPageInfo.path);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (isOnCreatePage) {
		return (
			<React.Fragment>
				<Button onClick={openModal} className={styles["message-group-list__new-chat"]}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem" className="nowrap">
						<Icon.Plus size={16} /> New Chat
					</Row>
				</Button>

				<Modal.If condition={isModalOpen}>
					<NewMessageModal onClose={closeModal} />
				</Modal.If>
			</React.Fragment>
		);
	}

	return (
		<InternalLink to={createPageInfo.to} className={styles["message-group-list__new-chat"]}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem" className="nowrap">
				<Icon.Plus size={16} /> New Chat
			</Row>
		</InternalLink>
	);
}

type MessageGroupViewProps = {
	message: LatestMessagesAndMessageGroupsListQuery["latestMessageByGroupConnection"]["edges"][0]["node"];
	showViewStatus: boolean;
	viewRecord: UserViewRecordListForTargetQuery["userViewRecordConnection"]["edges"][0]["node"] | null;
	messageGroupPageTo: (id: MessageGroup["id"]) => string;
};

function MessageGroupView({ message, showViewStatus, viewRecord, messageGroupPageTo }: MessageGroupViewProps) {
	const path = messageGroupPageTo(message.messageGroup.id);
	const matchesPath = useRouteMatch(path) !== null;

	const isUnread =
		!matchesPath && showViewStatus && (viewRecord === null || isBefore(viewRecord.lastViewedAt, message.createdAt));

	return (
		<NavLink
			to={path}
			className={styles["message-group-view"]}
			activeClassName={styles["message-group-view--location-active"]}
		>
			<Row justify="spaced-start" align="center">
				<MessageGroupIcon messageGroup={message.messageGroup} size={46} />

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ width: "100%", overflow: "hidden" }}>
					<Row justify="spaced-start">
						<TruncatedText className={styles["message-group-view__name"]}>
							{MessageGroupFormat.name(message.messageGroup)}
						</TruncatedText>
						<RelativeTimestamp
							value={message.createdAt}
							format={relativeTimeShort}
							className="timestamp"
							style={{ paddingRight: "1px" }} // padding because italic overflows
						/>
					</Row>

					<TruncatedText className={styles["message-group-view__message"]}>
						<Conditional>
							<If condition={message.isRemoved}>Message Removed</If>
							<If condition={message.body !== ""}>{message.body}</If>
							<If condition={message.mediaAttachments.length > 0}>
								{toGrammaticalNumber("Attachment", message.mediaAttachments.length, true)}
							</If>
						</Conditional>
					</TruncatedText>
				</Column>

				<If condition={isUnread}>
					<div className={styles["message-group-view__new-icon"]}></div>
				</If>
			</Row>
		</NavLink>
	);
}
